import { useEffect, useRef, useState } from 'react'

import {
  NotificationChannel,
  SpaceNotificationPreference,
} from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useSpaceDefaultNotificationSettings,
  useUpdateDefaultSpaceNotificationSettings,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { SectionHeader } from '@tribeplatform/react-ui-kit/SectionHeader'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { NotificationPreferences } from '../../../Notification/NotificationPerferences'
import { NotificationPreferencesChannelOverride } from '../../../Notification/NotificationPreferencesChannelOverride'
import { SpaceSettingsProps } from './types'

export const SpaceSettingsNotifications = ({
  space,
  onClose,
  isChangingTab,
  confirmTabChange,
  rejectTabChange,
  setDirty,
  setSubmit,
  isActive,
}: SpaceSettingsProps) => {
  const { $t } = useI18n()
  const [inAppPreference, setInAppPreference] =
    useState<SpaceNotificationPreference>()
  const [emailPreference, setEmailPreference] =
    useState<SpaceNotificationPreference>()
  const [emailSameAsDefault, setEmailSameAtDefault] = useState<boolean>()
  const buttonRef = useRef<HTMLButtonElement>()

  const { data: spaceDefaultNotificationSettings } =
    useSpaceDefaultNotificationSettings({
      variables: {
        spaceId: space?.id,
      },
    })
  const { mutateAsync: updateDefaultSpaceNotificationSettings, isLoading } =
    useUpdateDefaultSpaceNotificationSettings()

  const inAppSettings = spaceDefaultNotificationSettings?.find(
    it => it.channel === NotificationChannel.IN_APP,
  )
  const emailSettings = spaceDefaultNotificationSettings?.find(
    it => it.channel === NotificationChannel.EMAIL,
  )

  useEffect(() => {
    if (isActive) {
      setSubmit(() => () => buttonRef?.current?.click())
    }
  }, [isActive, setSubmit, buttonRef])

  useEffect(() => {
    if (isActive) {
      setDirty(
        (inAppPreference && inAppSettings?.preference !== inAppPreference) ||
          (emailPreference && emailSettings?.preference !== emailPreference) ||
          (emailSameAsDefault &&
            emailSettings?.sameAsDefault !== emailSameAsDefault),
      )
    }
  }, [
    isActive,
    setDirty,
    inAppSettings,
    emailSettings,
    inAppPreference,
    emailPreference,
    emailSameAsDefault,
  ])

  return (
    <Form
      onSubmit={async () => {
        try {
          if (inAppPreference) {
            await updateDefaultSpaceNotificationSettings({
              spaceId: space?.id,
              input: {
                preference: inAppPreference,
              },
              channel: inAppSettings.channel,
            })
          }

          if (emailPreference) {
            await updateDefaultSpaceNotificationSettings({
              spaceId: space?.id,
              input: {
                preference: emailPreference,
                sameAsDefault: emailSameAsDefault,
              },
              channel: emailSettings.channel,
            })
          }
          toast({
            title: $t({
              defaultMessage: 'Space default notification settings updated',
              id: 'Admin.NotificationSettings.SpaceDefaults.Updated',
            }),
            status: 'success',
          })
          if (isChangingTab) {
            confirmTabChange()
          } else {
            onClose()
          }
        } catch (e) {
          toast({
            title: $t({
              defaultMessage:
                'Unable to update space default notification settings',
              id: 'Admin.NotificationSettings.SpaceDefaults.FailedToUpdate',
            }),
            status: 'error',
          })
          if (isChangingTab) {
            rejectTabChange()
          }
        }
      }}
    >
      <div className="sm:h-132 flex flex-col">
        <div className="flex flex-col flex-grow overflow-auto p-5">
          <SectionHeader
            title={$t({
              defaultMessage: 'Notifications',
              id: 'Generics.Notifications',
            })}
          />
          <div className="space-y-5">
            <div>
              <FormControl.Label>
                <T
                  defaultMessage="Default notification settings"
                  id="Admin.NotificationSettings.Default"
                />
              </FormControl.Label>
              <FormControl.HelperText>
                <T
                  defaultMessage="Configure what the default notification setting should be when new users join this {SPACE}."
                  id="Admin.Space.Notifications.DefaultsForNewMembers"
                />
              </FormControl.HelperText>
            </div>
            <NotificationPreferences
              value={inAppPreference ?? inAppSettings?.preference}
              onChange={preference => {
                setInAppPreference(preference)
              }}
              context="defaultSettings"
            />
            <NotificationPreferencesChannelOverride
              notificationSettings={{
                ...emailSettings,
                preference: emailPreference ?? emailSettings?.preference,
                sameAsDefault:
                  emailSameAsDefault ?? emailSettings?.sameAsDefault,
              }}
              defaultValue={inAppSettings?.preference}
              onChange={(sameAsDefault, preference) => {
                setEmailPreference(preference)
                setEmailSameAtDefault(sameAsDefault)
              }}
            />
          </div>
        </div>
        <div className="flex justify-end px-5 py-5 border-t space-s-2">
          <Button type="button" variant="outline" onClick={() => onClose()}>
            <T defaultMessage="Cancel" id="Generics.Cancel" />
          </Button>
          <Button type="submit" loading={isLoading} ref={buttonRef}>
            <T defaultMessage="Update" id="Generics.Update" />
          </Button>
        </div>
      </div>
    </Form>
  )
}
